import { createFileRoute, isRedirect, redirect } from '@tanstack/react-router';
import { LoginPage } from '@/auth/LoginPage';
import { surviveCancel } from '@/api/loaders';
import { sessionMe } from '@/api/resources';
import { loginResume, parseLoginSearch } from '@/lib/oauth-consent';

export const Route = createFileRoute('/login')({
  // `?next=connect&request=<id>` (a consent request) or `?next=creative-assets&creative=<id>` (a
  // creative's files page, the link Claude hands a person) sent the person here to sign in, and
  // a sign-in returns them to it. `next` is an enum, never a URL; anything else is dropped.
  validateSearch: parseLoginSearch,
  // Route-data exemption: unauthenticated. There is nothing to warm before someone has a session.
  beforeLoad: async ({ context, search }) => {
    // If already authenticated, skip the login page.
    //
    // This used to re-declare the /me query verbatim — key, queryFn, staleTime
    // and retry — rather than call the guard that already owns it, so the four
    // lines had to be kept in step with `requireSession` by hand. Both now read
    // the same resource.
    try {
      const me = await surviveCancel(context.queryClient, sessionMe.key(undefined), () =>
        context.queryClient.fetchQuery(sessionMe(undefined)),
      );
      if (me) {
        const resume = loginResume(search);
        if (resume?.next === 'connect') throw redirect({ to: '/connect', search: { request: resume.request } });
        if (resume?.next === 'creative-assets') {
          throw redirect({ to: '/creatives/$creativeId/assets', params: { creativeId: resume.creative } });
        }
        throw redirect({ to: '/' });
      }
    } catch (err) {
      // redirect() throws — let it propagate. Anything else means no session; render login.
      // (`isRedirect`, not `'href' in err`: a redirect is a Response carrying `options`, with no
      // `href` of its own, so that test swallowed every redirect and a signed-in person stayed
      // on the login form.)
      if (isRedirect(err)) throw err;
    }
  },
  component: Page,
});

function Page() {
  return <LoginPage resume={loginResume(Route.useSearch())} />;
}
